/**
 * ============================================================
 * API 客户端 - 直接 fetch 调用 tRPC 后端
 * ============================================================
 * 后端连接 MySQL 数据库 (124.221.32.113 / jiesuandata)
 * 前端通过 fetch 调用 /api/trpc 端点
 * 保持原有函数签名不变
 * ============================================================
 */

import superjson from "superjson";
import type {
  User, DashboardStats, MonthlyTrend, PendingTask,
  Broker, BrokerStats, Formula, FormulaCategory, SettlementMonth,
  SettlementData, SettlementResult, SettlementSummary, ImportRecord,
  ReportTemplate, Role, RolePermission, PermissionConfig, Bill,
  BillStats, UserPermissions, FieldDefinition,
} from "./types";

const API_BASE = "/api/trpc";

function deserializeResponse<T>(data: unknown): T {
  if (!data || typeof data !== "object") return data as T;
  return superjson.deserialize(data as any) as T;
}

/** 获取请求头，包含 auth token */
function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = localStorage.getItem("token");
  if (token) headers["x-auth-token"] = token;
  return headers;
}

async function trpcCall<T>(path: string, input?: unknown): Promise<T> {
  const url = input !== undefined
    ? `${API_BASE}/${path}?input=${encodeURIComponent(JSON.stringify(superjson.serialize(input)))}`
    : `${API_BASE}/${path}`;
  console.log(`[trpcCall] GET ${url}`);
  console.log(`[trpcCall] headers:`, getHeaders());
  const res = await fetch(url, { headers: getHeaders(), credentials: "include" });
  console.log(`[trpcCall] response status:`, res.status);
  if (!res.ok) {
    const errorText = await res.text();
    console.error(`[trpcCall] API error: ${res.status}`, errorText);
    throw new Error(`API error: ${res.status}`);
  }
  const json = await res.json();
  console.log(`[trpcCall] response:`, json);
  return deserializeResponse<T>(json.result?.data);
}

/** 提取错误信息 */
function extractErrorMessage(errText: string): string {
  try {
    // 尝试解析JSON格式错误
    const parsed = JSON.parse(errText);
    if (parsed.error) {
      if (typeof parsed.error === 'string') {
        return parsed.error;
      }
      if (parsed.error.json && parsed.error.json.message) {
        return parsed.error.json.message;
      }
      if (parsed.message) {
        return parsed.message;
      }
    }
  } catch {
    // 不是JSON格式，尝试提取字符串中的错误信息
  }
  
  // 尝试从字符串中提取有用的错误信息
  const messageMatch = errText.match(/"message":"([^"]+)"/);
  if (messageMatch) {
    return messageMatch[1];
  }
  
  // 返回原始错误文本的前200个字符
  return errText.substring(0, 200);
}

async function trpcMutate<T>(path: string, input: unknown): Promise<T> {
  const body = JSON.stringify(superjson.serialize(input));
  console.log(`[trpcMutate] POST ${API_BASE}/${path}`);
  console.log(`[trpcMutate] body:`, body.substring(0, 500));
  const res = await fetch(`${API_BASE}/${path}`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
    body,
  });
  console.log(`[trpcMutate] response status:`, res.status);
  if (!res.ok) {
    const errText = await res.text();
    console.error(`[trpcMutate] error response:`, errText.substring(0, 500));
    const friendlyMessage = extractErrorMessage(errText);
    throw new Error(friendlyMessage || `操作失败`);
  }
  const json = await res.json();
  console.log(`[trpcMutate] response data:`, json);
  return deserializeResponse<T>(json.result?.data);
}

// ========== 认证 ==========
export const authApi = {
  register: (data: {
    username: string;
    password: string;
    name: string;
    name_real?: string;
    role?: string;
    card_type?: string;
    phone?: string;
    email?: string;
    id_card?: string;
    bank_card?: string;
    bank_name?: string;
    company_name?: string;
    bank_account_public?: string;
  }): Promise<{ success: boolean; user: User }> =>
    trpcMutate("auth.register", data),
  login: (username: string, password: string): Promise<{ token: string; user: User }> =>
    trpcMutate("auth.login", { username, password }),
  logout: (): Promise<void> => trpcMutate("auth.logout", {}),
  me: (): Promise<User> => trpcCall("auth.me"),
  profile: (data: Partial<User>): Promise<User> => trpcMutate("auth.profile", data),
  password: (data: { oldPassword: string; newPassword: string }): Promise<{ success: boolean }> => trpcMutate("auth.password", data),
  permissions: (): Promise<UserPermissions> => trpcCall("auth.permissions"),
  rolePermissions: (roleName: string): Promise<UserPermissions> => trpcCall("auth.rolePermissions", { roleName }),
};

// ========== 仪表盘 ==========
export const dashboardApi = {
  stats: (): Promise<DashboardStats> => trpcCall("dashboard.stats"),
  monthlyTrend: (): Promise<MonthlyTrend[]> => trpcCall("dashboard.monthlyTrend"),
  pendingTasks: (): Promise<PendingTask[]> => trpcCall("dashboard.pendingTasks"),
};

// ========== 经纪人 ==========
export const brokerApi = {
  list: (): Promise<Broker[]> => trpcCall("broker.list"),
  create: (data: Partial<Broker>): Promise<Broker> => trpcMutate("broker.create", data),
  update: (id: number, data: Partial<Broker>): Promise<Broker> => trpcMutate("broker.update", { id, ...data }),
  delete: (id: number): Promise<void> => trpcMutate("broker.delete", { id }),
  stats: (): Promise<BrokerStats> => trpcCall("broker.stats"),
  syncFromSettlements: (): Promise<{ success: boolean; added: number; skipped: number; total: number }> => trpcMutate("broker.syncFromSettlements", {}),
  cleanupDuplicates: (): Promise<{ success: boolean; deletedCount: number }> => trpcMutate("broker.cleanupDuplicates", {}),
  bind: (_id: number, _userId: number): Promise<void> => Promise.resolve(),
  unbind: (_id: number): Promise<void> => Promise.resolve(),
  autoCreate: (data: { name: string }): Promise<Broker> => brokerApi.create({ name: data.name, type: "个人", status: "active" }),
  availableUsers: (): Promise<User[]> => Promise.resolve([]),
  unboundUsers: (): Promise<User[]> => Promise.resolve([]),
  users: (): Promise<User[]> => Promise.resolve([]),
  monthlyData: (_id: number, _month: string): Promise<unknown> => Promise.resolve({}),
};

// ========== 公式 ==========
export const formulaApi = {
  list: (): Promise<Formula[]> => trpcCall("formula.list"),
  create: (data: { name: string; expression: string; variables?: string; description?: string }): Promise<Formula> => trpcMutate("formula.create", data),
  update: (id: number, data: Partial<Formula> & { description?: string }): Promise<Formula> => trpcMutate("formula.update", { id, ...data }),
  delete: (id: number): Promise<void> => trpcMutate("formula.delete", { id }),
  toggle: (id: number): Promise<void> => trpcMutate("formula.toggle", { id }),
  test: (expression: string, values?: Record<string, number | string>, brokerName?: string, month?: string): Promise<{ result: number; fieldValues: Record<string, any>; details?: any[] }> =>
    trpcMutate("formula.test", { expression, values, brokerName, month }),
  brokerList: (): Promise<{ name: string }[]> => trpcCall("formula.brokerList"),
  brokerMonths: (brokerName: string): Promise<string[]> => trpcCall("formula.brokerMonths", { brokerName }),
  brokerData: (brokerName: string, month?: string): Promise<Record<string, unknown> | null> => {
    const input = month ? { brokerName, month } : { brokerName };
    return trpcCall("formula.brokerData", input);
  },
  brokerMonthAnchors: (brokerName: string, month: string): Promise<Record<string, unknown>[]> => trpcCall("formula.brokerMonthAnchors", { brokerName, month }),
  validate: (expression: string): Promise<{ valid: boolean; fields: string[]; invalidFields: string[]; syntaxError: string }> =>
    trpcMutate("formula.validate", { expression }),
};

// ========== 结算 ==========
export const settlementApi = {
  months: (): Promise<SettlementMonth[]> => trpcCall("settlement.months"),
  report: (params?: { month?: string; brokerName?: string }): Promise<any[]> =>
    trpcCall("settlement.report", params),
  brokerAnchors: (params: { month: string; brokerName: string }): Promise<any[]> =>
    trpcCall("settlement.brokerAnchors", params),
  results: (_params?: { month?: string; brokerId?: number }): Promise<SettlementResult[]> =>
    trpcCall("settlement.results"),
  summary: (_params?: { month?: string; brokerId?: number }): Promise<SettlementSummary[]> =>
    trpcCall("settlement.summary"),
  calculate: (_data: { brokerId: number; month: string; formulaId?: number }): Promise<void> => Promise.resolve(),
  batchCalculate: (_data: { month: string; brokerIds?: number[] }): Promise<void> => Promise.resolve(),
  clear: (_data?: { month?: string; brokerId?: number }): Promise<void> => trpcMutate("settlement.clear", {}),
  importBatch: (rows: Record<string, unknown>[]): Promise<{ success: boolean; count: number }> =>
    trpcMutate("settlement.importBatch", { rows }),
};

// ========== 导入 ==========
export const importApi = {
  list: (): Promise<ImportRecord[]> => trpcCall("importRecord.list"),
  upload: (_file: File): Promise<unknown> => Promise.resolve({ importId: Date.now() }),
  preview: (_file: File): Promise<unknown> => Promise.resolve([]),
  create: (data: { fileName: string; rowCount: number; headers: string[] }): Promise<ImportRecord> =>
    trpcMutate("importRecord.create", data),
  delete: (id: number): Promise<void> => trpcMutate("importRecord.delete", { id }),
};

// ========== 账单模板 ==========
export const reportApi = {
  templates: (): Promise<ReportTemplate[]> => trpcCall("reportTemplate.list"),
  get: (id: number): Promise<ReportTemplate> => trpcCall("reportTemplate.get", { id }),
  create: (data: Partial<ReportTemplate>): Promise<ReportTemplate> => trpcMutate("reportTemplate.create", data),
  update: (id: number, data: Partial<ReportTemplate>): Promise<ReportTemplate> => trpcMutate("reportTemplate.update", { id, ...data }),
  delete: (id: number): Promise<void> => trpcMutate("reportTemplate.delete", { id }),
  generate: (_data: { templateId: number; month: string; brokerIds?: number[] }): Promise<void> => Promise.resolve(),
};

// ========== 用户 ==========
export const userApi = {
  list: (): Promise<User[]> => trpcCall("user.list"),
  getById: (id: number): Promise<User | null> => trpcCall("user.getById", { id }),
  create: (data: Partial<User>): Promise<User> => trpcMutate("user.create", data),
  update: (id: number, data: Partial<User>): Promise<User> => trpcMutate("user.update", { id, ...data }),
  delete: (id: number): Promise<void> => trpcMutate("user.delete", { id }),
};

// ========== 角色管理 ==========
export const roleApi = {
  list: (): Promise<Role[]> => trpcCall("role.list"),
  create: (data: { name: string; description: string; permissions: PermissionConfig[] }): Promise<Role> =>
    trpcMutate("role.create", data),
  update: (id: number, data: { name: string; description: string; permissions: PermissionConfig[] }): Promise<void> =>
    trpcMutate("role.update", { id, ...data }),
  delete: (id: number): Promise<void> => trpcMutate("role.delete", { id }),
  permissions: (id: number): Promise<RolePermission[]> => trpcCall("role.permissions", { id }),
};

// ========== 账单管理 ==========
export const billApi = {
  list: (filter?: Record<string, unknown>): Promise<{ data: Bill[]; total: number }> => trpcCall("bill.list", filter),
  create: (data: Partial<Bill>): Promise<Bill> => trpcMutate("bill.create", data),
  createManual: (data: { brokerId: number; settlementMonth: string; shareAmount: number }): Promise<Bill> => 
    trpcMutate("bill.createManual", data),
  confirm: (id: number): Promise<void> => trpcMutate("bill.confirm", { id }),
  pay: (id: number): Promise<void> => trpcMutate("bill.pay", { id }),
  reject: (id: number, reason?: string): Promise<void> => trpcMutate("bill.reject", { id, reason }),
  invoice: (id: number, invoiceUrl: string, fileType: string): Promise<void> => trpcMutate("bill.invoice", { id, invoiceUrl, invoiceFileType: fileType }),
  deleteInvoice: (id: number): Promise<void> => trpcMutate("bill.deleteInvoice", { id }),
  delete: (id: number): Promise<void> => trpcMutate("bill.delete", { id }),
  upload: (file: File): Promise<{ url: string; fileType: string }> => trpcMutate("bill.upload", file),
  generateFromSettlements: (month?: string): Promise<{ success: boolean; created: number; updated: number; total: number }> => trpcMutate("bill.generateFromSettlements", { month }),
  getAvailableMonths: (): Promise<{ months: string[] }> => trpcCall("bill.getAvailableMonths"),
  stats: (params?: { month?: string; status?: string }): Promise<BillStats> => trpcCall("bill.stats", params),
  wageDeclare: (id: number): Promise<void> => trpcMutate("bill.wageDeclare", { id }),
  wageUndeclare: (id: number): Promise<void> => trpcMutate("bill.wageUndeclare", { id }),
};

// ========== 字段定义 ==========
export const fieldDefinitionApi = {
  list: (): Promise<FieldDefinition[]> => trpcCall("fieldDefinition.list"),
  byImport: (importId: number): Promise<FieldDefinition[]> => trpcCall("fieldDefinition.byImport", { importId }),
  create: (data: Partial<FieldDefinition>): Promise<FieldDefinition> => trpcMutate("fieldDefinition.create", data),
  createBatch: (data: Partial<FieldDefinition>[]): Promise<{ success: boolean }> =>
    trpcMutate("fieldDefinition.createBatch", data),
  delete: (id: number): Promise<void> => trpcMutate("fieldDefinition.delete", { id }),
};

// ========== 字段映射 (兼容性) ==========
export const fieldMappingApi = {
  list: (): Promise<unknown> => Promise.resolve([]),
};

// ========== 健康检查 ==========
export const healthApi = {
  check: (): Promise<unknown> => Promise.resolve({ status: "ok" }),
};

// ========== 清空数据 ==========
export const clearDataApi = {
  getTables: (): Promise<{ name: string; label: string; description: string; warning: string }[]> => 
    trpcCall("clearData.getTables"),
  clearTable: (tableName: string): Promise<{ success: boolean; tableName: string }> => 
    trpcMutate("clearData.clearTable", { tableName }),
  clearAll: (): Promise<{ success: boolean }> => trpcMutate("clearData.clearAll"),
};

// ========== 清空数据库 (兼容性) ==========
export function clearDatabase(): number {
  return 0;
}
